/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IEnvironmentConfig } from '../../../../environments/environment.interface';
import { APP_CONFIG } from '../../../core/config/app-config.token';
import { IDeviceHealthCheckLiveChatParams } from '../../../device-health-check/models';

/**
 * Service for loading and managing Amazon Connect chat widget.
 *
 * Implementation Pattern:
 * - Amazon Connect uses a command queue pattern where window['amazon_connect'] calls
 *   are queued BEFORE the script loads, then processed once the script is available.
 * - This allows configuration (snippetId, styles, customization) to be set up synchronously
 *   before the async script load completes.
 * - The widget lifecycle: setup queue → load script → process queue → render widget
 *
 * Why this structure:
 * - _setAmazonConnectArguments() must be called BEFORE script loads to queue commands
 * - _setContactAttributes() sets chat metadata (accountId, vehicleName, etc.)
 * - Script loading is async; onload callback updates isLoaded$ observable
 * - unload() cleans up DOM elements and resets state for subsequent loads
 */
@Injectable()
export class AmazonConnectChatLoaderService {
  public readonly isLoaded$ = new BehaviorSubject(false);

  private readonly WIDGET_WIDTH = 360;
  private readonly WIDGET_HEIGHT = 630;

  constructor(
    @Inject(APP_CONFIG) private readonly _envConfig: IEnvironmentConfig,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  public load(contactAttributes: IDeviceHealthCheckLiveChatParams): void {
    if (this.isLoaded$.value) {
      return;
    }

    const script = this._document.createElement('script');

    script.src = this._envConfig.deviceHealthCheckAmazonConnectChat.scriptUrl;
    script.id = this._envConfig.deviceHealthCheckAmazonConnectChat.instanceId;
    script.async = true;

    script.onload = () => {
      this.isLoaded$.next(true);
      this._setWidgetElementClass();
    };

    script.onerror = () => {
      console.error('Failed to load Amazon Connect script');
    };

    this._setAmazonConnectArguments();
    this._setContactAttributes(contactAttributes);
    this._document.head.appendChild(script);
  }

  private _setAmazonConnectArguments(): void {
    const window = this._document.defaultView as any;

    window['amazon_connect'] =
      window['amazon_connect'] ||
      function () {
        (window['amazon_connect'].ac = window['amazon_connect'].ac || []).push(arguments);
      };

    window['amazon_connect']('snippetId', this._envConfig.deviceHealthCheckAmazonConnectChat.snippetId);
    window['amazon_connect']('styles', {
      iconType: 'CHAT',
      openChat: {
        color: '#ffffff',
        backgroundColor: '#000000'
      },
      closeChat: {
        color: '#ffffff',
        backgroundColor: '#000000'
      }
    });
    window['amazon_connect']('supportedMessagingContentTypes', [
      'text/plain',
      'text/markdown',
      'application/vnd.amazonaws.connect.message.interactive',
      'application/vnd.amazonaws.connect.message.interactive.response'
    ]);
    window['amazon_connect']('customizationObject', {
      header: {
        dropdown: true,
        dynamicHeader: false,
        hideTranscriptDownloadButton: false
      },
      footer: {
        disabled: false,
        enableConfirmationDialog: true
      },
      composer: {
        alwaysHideToolbar: true
      },
      transcript: {
        eventMessages: {
          participantLeft: '{name} has left the chat.',
          chatEnded: 'The chat has ended.'
        }
      }
    });
    window['amazon_connect']('customStyles', {
      global: {
        frameWidth: `${this.WIDGET_WIDTH}px`,
        frameHeight: `${this.WIDGET_HEIGHT}px`
      }
    });
    window['amazon_connect']('customLaunchBehavior', {
      skipIconButtonAndAutoLaunch: true,
      alwaysHideWidgetButton: true
    });
    window['amazon_connect']('registerCallback', {
      WIDGET_FRAME_CLOSED: () => {
        this.unload();
      }
    });
    window['amazon_connect']('mockLexBotTyping', true);
  }

  private _setContactAttributes(params: IDeviceHealthCheckLiveChatParams): void {
    const window = this._document.defaultView as any;
    window['amazon_connect']('contactAttributes', {
      accountId: params.accountId,
      vehicleName: params.vehicleName,
      trackerType: params.trackerType,
      esn: params.ESN,
      lastUpdate: params.lastUpdate,
      status: params.status
    });
  }

  private _setWidgetElementClass(): void {
    const instanceId = this._envConfig.deviceHealthCheckAmazonConnectChat.instanceId;
    const widgetElement = this._document.getElementById('amazon-connect-chat-widget');
    if (widgetElement) {
      widgetElement.classList.add(`amazon-connect-chat-widget-${instanceId}`);
    }
  }

  public unload(): void {
    const window = this._document.defaultView as any;
    window['amazon_connect'] = null;

    const widgetElements = this._document.getElementsByClassName(
      `amazon-connect-chat-widget-${this._envConfig.deviceHealthCheckAmazonConnectChat.instanceId}`
    );
    Array.from(widgetElements).forEach(element => element.remove());
    this._document.getElementById(this._envConfig.deviceHealthCheckAmazonConnectChat.instanceId)?.remove();

    this.isLoaded$.next(false);
  }
}
